// @ts-nocheck
import React, { useEffect, useRef } from 'react';

interface ProxiedIframeProps {
    url: string;
    className?: string;
    style?: React.CSSProperties;
    title?: string;
    sandbox?: string;
}

/**
 * Renders an external URL inside an iframe via the /api/proxy endpoint,
 * which strips X-Frame-Options/CSP headers server-side.
 *
 * Also runs a 300ms polling loop that detects when the embedded site's
 * JavaScript navigates the iframe away from the proxy URL (e.g. via
 * window.location.href = '/' or history.pushState) and immediately
 * snaps it back through the proxy.
 */
const ProxiedIframe: React.FC<ProxiedIframeProps> = ({
    url,
    className = '',
    style,
    title,
    sandbox = 'allow-scripts allow-same-origin allow-forms allow-popups allow-downloads',
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const rawUrl = url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;
        
    // Extract base URL of backend from getApiUrl (which is dynamic and handles Native/Capacitor vs Web hosts)
    const getProxyBase = () => {
        const isDevServer = window.location.port === '3000';
        if (isDevServer) {
            return `${window.location.protocol}//${window.location.hostname}:5000`;
        }
        
        // Native apps (Capacitor) run on a local server scheme (e.g. capacitor://localhost or http://localhost)
        // We must point their proxy base strictly to the live production server (or local tunnel in dev)
        const envUrl = process.env.REACT_APP_SERVER_URL;
        if (envUrl && envUrl.trim().length > 0) {
            return envUrl.trim().replace(/\/+$/, '');
        }
        
        // Capacitor check matching api.ts logic
        const isNative = (window as any).Capacitor !== undefined || window.location.href.startsWith('capacitor://');
        if (isNative) {
            return process.env.NODE_ENV === 'development' ? 'https://goodlyfe-dev.loca.lt' : 'https://goodlyfe.net';
        }
        
        // Standard Web relative path (same origin)
        return '';
    };

    const proxyBase = getProxyBase();
    
    let proxyUrl = '';
    try {
        const parsed = new URL(rawUrl);
        const proto = parsed.protocol.replace(':', '');
        const rest = parsed.host + parsed.pathname + parsed.search + parsed.hash;
        proxyUrl = `${proxyBase}/api/proxy/${proto}/${rest}`;
    } catch (e) {
        proxyUrl = `${proxyBase}/api/proxy?url=${encodeURIComponent(rawUrl)}`;
    }

    return (
        <iframe
            ref={iframeRef}
            src={proxyUrl}
            className={className}
            style={style}
            sandbox={sandbox}
            allow="camera; microphone; geolocation"
            title={title ?? rawUrl}
        />
    );
};

export default ProxiedIframe;
